import { z } from 'zod';

import { NotificationTypeSchema } from '../notification/types';
import { NotificationCtaSchema } from './notification_cta';

/**
 * Inbox grouping category — the visual anchor axis of the notifications
 * panel, ORTHOGONAL to `notificationType` (many types map to one category).
 *
 * D-02: only categories with a live producer in this milestone exist.
 * `document` | `continuity` | `social` join when a real producer ships —
 * never pre-created "for future use".
 */
export const NotificationInboxCategorySchema = z.enum(['flight', 'status']);
export type NotificationInboxCategory = z.infer<typeof NotificationInboxCategorySchema>;

/**
 * Event valence of an inbox item — drives the SATELLITE badge on the visual
 * anchor (the client owns shape+color per kind; the server only names the
 * kind). CLOSED vocabulary by construction: an event outside these five
 * renders WITHOUT a satellite — producers must not invent kinds, they either
 * map to one of these or omit `presentation.event` entirely.
 */
export const NotificationEventKindSchema = z.enum([
  'approved',
  'rejected',
  'delay',
  'info_change',
  'reschedule',
]);
export type NotificationEventKind = z.infer<typeof NotificationEventKindSchema>;

/**
 * How hard an inbox item presses on the person (#6333) — the order and the mark
 * of the home's "Precisa de você" stack: `critical` jumps the queue and carries
 * the destructive dot (a cancelled flight), `normal` is a plain pending item,
 * `info` asks for nothing (a gate change). Stamped by the conversation worker per
 * type/alert; the client may still RAISE an approval to `critical` from the
 * approvals queue's `expiresAt` (it moves with the clock, the stamp does not).
 */
export const NotificationSeveritySchema = z.enum(['critical', 'normal', 'info']);
export type NotificationSeverity = z.infer<typeof NotificationSeveritySchema>;

/**
 * Booking modal of an inbox item — drives the BASE glyph of the visual anchor
 * (plane / bed / car) when the item is a reservation rather than a flight
 * alert. CLOSED by construction, D-02 style: only the modals a live producer
 * stamps today (the voucher `modal` field maps FLIGHT/CONCIERGE_FLIGHT →
 * `flight`, HOTEL/CONCIERGE_HOTEL → `hotel`, RENTAL → `rental`); a voucher
 * outside the map ships WITHOUT `modal` and the client falls back to the
 * category glyph. Distinct from `ModalSessionModalSchema` (a chat-session
 * axis that also knows `mobility`) — do not merge the two.
 */
export const NotificationInboxModalSchema = z.enum(['flight', 'hotel', 'rental']);
export type NotificationInboxModal = z.infer<typeof NotificationInboxModalSchema>;

/**
 * The kind of one structured FACT under an inbox title — the client owns the
 * glyph and the emphasis per kind (a date and an amount read strong, a policy
 * breach reads as a warning, a deadline as critical); the server only names
 * the kind and renders the localized `value`. CLOSED vocabulary: a kind
 * outside this list is dropped by the wire schema, never invented ad hoc.
 */
export const NotificationFactKindSchema = z.enum([
  // Travel dates ("qua 21 a qui 22 out").
  'period',
  // Whose booking it is — stamped only when traveler ≠ requester.
  'traveler',
  // The code the traveler uses (VOLL voucher or supplier locator).
  'code',
  // The total, already formatted in the item locale ("R$ 2.868,03").
  'amount',
  // Airline / hotel chain / rental company.
  'supplier',
  // A place when the route is not in the title (a hotel's city).
  'place',
  // Policy verdict, emitted only when the booking is OUT of policy.
  'policy',
  // Approval expiry ("Expira 13 set, 16:38").
  'deadline',
]);
export type NotificationFactKind = z.infer<typeof NotificationFactKindSchema>;

/**
 * One fact of an inbox item: `value` is localized ON THE SERVER (same locale
 * as title/detail) and rendered verbatim by clients, like `NotificationCta.label`.
 */
export const NotificationFactSchema = z
  .object({ kind: NotificationFactKindSchema, value: z.string().min(1) })
  .strict();
export type NotificationFact = z.infer<typeof NotificationFactSchema>;

/**
 * The flight leg a live flight alert is about (#6277): the carrier's IATA code
 * and the flight number as the source sends them, plus the airports when known.
 * Stamped only when the producer sent carrier AND number. Together with
 * `presentation.tripId` it is the key that puts every alert of one flight in one
 * conversation, and the client composes «{cia} {voo} · {origem} → {destino}» from it.
 */
export const NotificationFlightSchema = z
  .object({
    carrier: z.string().min(1),
    number: z.string().min(1),
    from: z.string().min(1).optional(),
    to: z.string().min(1).optional(),
  })
  .strict();
export type NotificationFlight = z.infer<typeof NotificationFlightSchema>;

/** Upper bound of `facts` on the wire — the card shows a 2-column grid. */
export const NOTIFICATION_FACTS_MAX = 8;

/**
 * One inbox entry as served to clients — the WIRE projection, not the Mongo
 * document (the persisted `notification_inbox` doc lives with the
 * conversation service and carries `_schemaVersion`; this shape does not).
 *
 * `id` is the conversation-side `_id`: the sha256 of the originating
 * externalMessageId — the SAME value used by the messages doc, so both
 * collections share one dedup fence.
 *
 * `readAt`/`resolvedAt` are nullable, NOT optional: the lifecycle is always
 * on the wire and `null` means "not yet". Optional would make "unread"
 * ambiguous with "producer forgot the field".
 *
 * `conversationId` is the navigation fallback when the event landed in a
 * conversation. ABSENT when no delivery identity resolved at the time of the
 * event (mandatory-inbox rule: the inbox item is written even when every
 * delivery channel degraded, and no conversation exists to point at) — each
 * entry in `ctas` carries its own `conversationId` whenever one exists.
 *
 * `ctas` is an ORDERED list (first entry = primary action, max 3). Legacy
 * persisted docs carry a singular `cta` — the conversation service PROJECTS
 * them onto this list (`cta` → `[cta]`) before they reach the wire, so the
 * singular field never appears here.
 *
 * `facts` is the STRUCTURED body of the card (ordered, max
 * `NOTIFICATION_FACTS_MAX`): each entry names a closed `kind` the client maps
 * to a glyph + emphasis, with the localized `value` rendered verbatim. It
 * REPLACES the free-text `detail` DERIVED FROM THE CHAT BODY — that fallback
 * would reprint what the grid already shows. `detail` is NOT mutually
 * exclusive with `facts`: a purpose-written one (the seeded `inboxDetail`)
 * still rides alongside the grid, and the client renders both.
 *
 * `presentation` carries optional display anchors (airline identity, booking
 * modal, trip binding, event valence for the satellite badge, the flight leg
 * of a live flight alert) so the client renders the visual anchor without
 * re-fetching.
 */
export const NotificationInboxItemSchema = z
  .object({
    id: z.string().min(1),
    notificationType: NotificationTypeSchema,
    category: NotificationInboxCategorySchema,
    title: z.string().min(1),
    detail: z.string().optional(),
    facts: z.array(NotificationFactSchema).max(NOTIFICATION_FACTS_MAX).optional(),
    createdAt: z.iso.datetime(),
    readAt: z.iso.datetime().nullable(),
    resolvedAt: z.iso.datetime().nullable(),
    // True while the item demands something from the user — drives the
    // "need you" tab and the `pending` counter (pending = requiresAction
    // && resolvedAt === null).
    requiresAction: z.boolean(),
    // Optional AND opt-in: the list read carries it only when the client asks
    // (`?severity=true` on the BFF). Clients cached before the field validate the
    // item with `.strict()` and would drop it whole; push frames never carry it.
    severity: NotificationSeveritySchema.optional(),
    ctas: z.array(NotificationCtaSchema).max(3).optional(),
    conversationId: z.string().min(1).optional(),
    presentation: z
      .object({
        airline: z.object({ iata: z.string(), name: z.string() }).optional(),
        modal: NotificationInboxModalSchema.optional(),
        tripId: z.string().optional(),
        event: NotificationEventKindSchema.optional(),
        flight: NotificationFlightSchema.optional(),
      })
      .strict()
      .optional(),
  })
  .strict();
export type NotificationInboxItem = z.infer<typeof NotificationInboxItemSchema>;

/**
 * Bell-badge counters. `unread` = items with `readAt === null`; `pending` =
 * items with `requiresAction && resolvedAt === null`. Served by the counts
 * endpoint and riding along on every push frame so the badge never needs the
 * full list.
 */
export const NotificationCountsSchema = z
  .object({ unread: z.number().int().min(0), pending: z.number().int().min(0) })
  .strict();
export type NotificationCounts = z.infer<typeof NotificationCountsSchema>;

/**
 * One page of the inbox list (createdAt desc). `nextCursor` is an OPAQUE
 * pagination token owned by the conversation repository; absent on the last
 * page.
 */
export const NotificationInboxListSchema = z
  .object({ items: z.array(NotificationInboxItemSchema), nextCursor: z.string().optional() })
  .strict();
export type NotificationInboxList = z.infer<typeof NotificationInboxListSchema>;

/**
 * The conversation an inbox item opens as (#6277) — `POST
 * /notifications/:id/conversation`. Every item opens as a conversation of the
 * traveler's app channel: the one the event was delivered in when that was the
 * app, otherwise the item's own thread, where every alert of the same flight
 * lands. Opening only marks the item read; `resolvedAt` stays with the action.
 */
export const NotificationConversationSchema = z
  .object({ conversationId: z.string().min(1) })
  .strict();
export type NotificationConversation = z.infer<typeof NotificationConversationSchema>;
